import React from "react";
import _ from "lodash";
import Select from "react-select";

import NumberFormat from "components/common/NumberFormat";
import PriceUnit from "components/ops-mgmt/account/upr-segmentation/PriceUnit";
import SimpleTooltip from "components/common/SimpleTooltip";
import { formatMoneyWithCurrency } from "components/intowow-business/platform-index/PlatformIndexReportsHelper";
import { IoIosArrowDown } from "react-icons/io";

import { AnatomyCSTContext } from "./context/AnatomyCSTContext";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 shadow hover:bg-gray-300 text-gray-900 font-semibold";

const ORDER_OPTION = {
  TOTAL_REV_ASC: "total_rev@asc",
  TOTAL_REV_DESC: "total_rev@desc",
  SEGMENTAION_ASC: "upr_segmentation_id:upr_segment_id@asc:asc",
  SEGMENTAION_DESC: "upr_segmentation_id:upr_segment_id@desc:desc",
};

class AnatomyCstSegmentsTable extends React.PureComponent {
  static contextType = AnatomyCSTContext;

  constructor(props) {
    super(props);

    this.state = {
      selectedSegmentId: null,

      orderOptions: [
        {
          value: ORDER_OPTION.TOTAL_REV_DESC,
          label: "Total Revenue (Descending)",
        },
        {
          value: ORDER_OPTION.TOTAL_REV_ASC,
          label: "Total Revenue (Ascending)",
        },
        {
          value: ORDER_OPTION.SEGMENTAION_DESC,
          label: "Segmentation ID (Descending)",
        },
        {
          value: ORDER_OPTION.SEGMENTAION_ASC,
          label: "Segmentation ID (Ascending)",
        },
      ],
      selectedOrder: ORDER_OPTION.TOTAL_REV_DESC,
    };

    this.handleSelectSegment = this.handleSelectSegment.bind(this);
  }

  handleSelectSegment(selectedSegmentId) {
    this.setState({
      selectedSegmentId,
    });
    this.props.handleGetSegmentReport(selectedSegmentId);
  }

  render() {
    const { selectedSegmentId, orderOptions, selectedOrder } = this.state;
    const { items, segmentTotalReport, selectedDateRange } = this.props;
    const { currency } = this.context;

    if (items.length === 0) {
      return <div>No Segments</div>;
    }

    const splitOrder = selectedOrder.split("@");

    const parsedSegmentTotalReport = segmentTotalReport.map((item) => ({
      ...item,
      uprSegmentationId: _.isNil(item.upr_segmentation_id)
        ? "N/A"
        : item.upr_segmentation_id,
      uprSegmentId: _.isNil(item.upr_segment_id) ? -1 : item.upr_segment_id,
      name: _.isNil(item.name) ? "Unsegmented Traffic" : item.name,
    }));

    const sortedTotalReport = _.orderBy(
      parsedSegmentTotalReport,
      splitOrder[0].split(":"),
      splitOrder[1].split(":")
    );

    const parsedOption = _.find(orderOptions, {
      value: selectedOrder,
    });

    const isSortBySegmentation = [
      ORDER_OPTION.SEGMENTAION_ASC,
      ORDER_OPTION.SEGMENTAION_DESC,
    ].includes(selectedOrder);

    return (
      <div>
        <div className="mb-1 flex justify-between">
          <div className="pt-4 font-medium">{items.length} segments</div>
          <Select
            className="w-1/5"
            isSearchable={false}
            value={parsedOption}
            onChange={({ value }) => this.setState({ selectedOrder: value })}
            options={orderOptions}
          ></Select>
        </div>

        <table className="border shadow table w-full text-sm">
          <thead className="border-b bg-gray-200 text-xs text-blue-800">
            <tr>
              {isSortBySegmentation && (
                <th className="border px-2 py-1 text-center" rowSpan={2}>
                  Segmentation ID
                </th>
              )}
              <th className="border px-2 py-1 text-center" rowSpan={2}>
                UPR Segment ID
              </th>

              <th className="border p-2 text-center" colSpan={6}>
                Past {selectedDateRange} days performance
              </th>
              <th className="border px-2 py-1 text-center" rowSpan={2}>
                Optimization
              </th>
              <th className="border px-2 py-1 text-center" rowSpan={2}>
                Traffic Scope
              </th>
              <th
                className="border px-2 py-1 text-left"
                style={{ width: "25rem" }}
                rowSpan={2}
              >
                Prices
              </th>
              <th className="border px-2 py-1 text-center" rowSpan={2}>
                Action
              </th>
            </tr>

            <tr>
              <th className="border p-2 text-right font-semibold text-gray-900">
                Gross Incr Lift
              </th>
              <th className="border p-2 text-right font-semibold text-gray-900">
                Net Incr Lift
              </th>
              <th className="border flex justify-end p-2 font-semibold text-gray-900">
                <div className="flex items-center gap-2">
                  Total Rev
                  <IoIosArrowDown></IoIosArrowDown>
                </div>
              </th>
              <th className="border p-2 text-right font-semibold text-gray-900">
                Original Rev
              </th>
              <th className="border p-2 text-right font-semibold text-gray-900">
                Incr Rev
              </th>
              <th className="border p-2 text-right font-semibold text-gray-900">
                Cost
                <SimpleTooltip tooltipText="Cost is calculated with all demand types">
                  *
                </SimpleTooltip>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white text-gray-800">
            {sortedTotalReport.map((item, idx) => {
              const shouldHighlight = item.isImportant; //item.total_rev_ratio > 1; // at least 1%

              return (
                <tr
                  key={item.uprSegmentId}
                  className={`border-b hover:bg-gray-100 ${
                    selectedSegmentId === item.uprSegmentId
                      ? "bg-blue-100 hover:bg-blue-100"
                      : item.deprecated
                      ? "bg-orange-100"
                      : "bg-white"
                  }`}
                >
                  {isSortBySegmentation &&
                    (idx === 0 ||
                      (idx > 0 &&
                        sortedTotalReport[idx - 1].uprSegmentationId !==
                          item.uprSegmentationId)) && (
                      <td
                        className="border bg-white px-2 py-1 text-center"
                        rowSpan={
                          sortedTotalReport.filter(
                            (seg) =>
                              seg.uprSegmentationId === item.uprSegmentationId
                          ).length
                        }
                      >
                        {item.uprSegmentationId}
                      </td>
                    )}
                  <td
                    className={`border px-2 py-1 text-center ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    {item.deprecated && (
                      <div className="text-orange-600">[deprecated]</div>
                    )}
                    <div className={`${item.deprecated ? "underline" : ""}`}>
                      {item.uprSegmentId}
                    </div>
                    <div>{item.name}</div>
                    {!isSortBySegmentation && (
                      <div>Segmentation ID: {item.uprSegmentationId}</div>
                    )}
                  </td>

                  <td
                    className={`border px-2 py-1 text-right ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    <span
                      className={
                        shouldHighlight &&
                        (item.gross_lift >= 0
                          ? "text-green-600"
                          : "text-red-600")
                      }
                    >
                      {item.gross_lift > 0 && "+"}
                      <NumberFormat
                        value={item.gross_lift}
                        format="0,0.00%"
                      ></NumberFormat>
                    </span>
                  </td>
                  <td
                    className={`border px-2 py-1 text-right ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    <span
                      className={
                        shouldHighlight &&
                        (item.net_lift >= 0 ? "text-green-600" : "text-red-600")
                      }
                    >
                      {item.net_lift > 0 && "+"}
                      <NumberFormat
                        value={item.net_lift}
                        format="0,0.00%"
                      ></NumberFormat>
                    </span>
                  </td>
                  <td
                    className={`border px-2 py-1 text-right ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    {formatMoneyWithCurrency({
                      currency,
                      value: item.total_rev || 0,
                    })}
                    <br></br>(
                    <NumberFormat
                      value={item.total_rev_ratio}
                      format="0,0.00"
                      postfix="%"
                    ></NumberFormat>
                    )
                  </td>
                  <td
                    className={`border px-2 py-1 text-right ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    {formatMoneyWithCurrency({
                      currency,
                      value: item.original_rev || 0,
                    })}
                  </td>

                  <td
                    className={`border px-2 py-1 text-right ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    <span
                      className={
                        shouldHighlight &&
                        (item.increased_rev >= 0
                          ? "text-green-600"
                          : "text-red-600")
                      }
                    >
                      {formatMoneyWithCurrency({
                        currency,
                        value: item.increased_rev || 0,
                      })}
                    </span>
                  </td>
                  <td
                    className={`border px-2 py-1 text-right ${
                      shouldHighlight && "font-semibold"
                    }`}
                  >
                    {formatMoneyWithCurrency({
                      currency,
                      value: item.cost || 0,
                    })}
                  </td>

                  <td className="border px-2 py-1 text-center font-semibold">
                    {item.optimization ? (
                      <span
                        className={
                          _.startsWith(item.optimization, "cstv2")
                            ? "text-teal-600"
                            : "text-gray-600"
                        }
                      >
                        {item.optimization}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td className="border px-2 py-1 text-center font-semibold">
                    {item.traffic_scope ? (
                      <span
                        className={
                          item.traffic_scope === "NO_PREBID"
                            ? "text-purple-600"
                            : "text-gray-600"
                        }
                      >
                        {item.traffic_scope}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td className="border px-1 py-1 text-left text-xs">
                    <div className="border-b mb-1">
                      <span className="font-semibold text-gray-800">
                        Prices:
                        {item.optimization !== "dt" && item.prices && (
                          <span>({item.prices.length})</span>
                        )}
                      </span>
                      {item.optimization !== "dt" && item.prices ? (
                        <div className="flex w-full flex-wrap gap-1 pb-1">
                          {item.prices.map((p, idx) => {
                            return (
                              <PriceUnit
                                price={p}
                                key={`${idx}-${p}`}
                                readOnly={true}
                                tooltip={idx + 1}
                              ></PriceUnit>
                            );
                          })}
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>

                    <div className="border-b mb-1">
                      <span className="font-semibold text-gray-800">
                        Detector Prices:{" "}
                        {item.detector_prices && (
                          <>({item.detector_prices.length})</>
                        )}
                      </span>
                      {item.detector_prices ? (
                        <div className="flex w-full flex-wrap gap-1 pb-1">
                          {item.detector_prices.map((p, idx) => {
                            return (
                              <PriceUnit
                                price={p}
                                key={`${idx}-${p}`}
                                readOnly={true}
                                tooltip={idx + 1}
                              ></PriceUnit>
                            );
                          })}
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>

                    <div className="border-b mb-1">
                      <span className="font-semibold text-gray-800">
                        Catchall Prices:{" "}
                        {item.catchall_prices && (
                          <>({item.catchall_prices.length})</>
                        )}
                      </span>
                      {item.catchall_prices ? (
                        <div className="flex w-full flex-wrap gap-1 pb-1">
                          {item.catchall_prices.map((p, idx) => {
                            return (
                              <PriceUnit
                                price={p}
                                key={`${idx}-${p}`}
                                readOnly={true}
                                tooltip={idx + 1}
                              ></PriceUnit>
                            );
                          })}
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>

                    <div>
                      <span className="font-semibold text-gray-800">
                        X Prices:{" "}
                        {item.x_prices && <>({item.x_prices.length})</>}
                      </span>
                      {item.x_prices ? (
                        <div className="flex w-full flex-wrap gap-1 pb-1">
                          {item.x_prices.map((p, idx) => {
                            return (
                              <PriceUnit
                                price={p}
                                key={`${idx}-${p}`}
                                readOnly={true}
                                tooltip={idx + 1}
                              ></PriceUnit>
                            );
                          })}
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>
                  </td>

                  <td className="whitespace-no-wrap border px-2 py-1 text-right">
                    <div className="flex items-center justify-end gap-4">
                      <button
                        type="button"
                        className={buttonActionClass}
                        onClick={() =>
                          this.handleSelectSegment(item.uprSegmentId)
                        }
                      >
                        Get report
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default AnatomyCstSegmentsTable;
